import { useMutation } from '@tanstack/react-query'
import type { DictationReviewSnapshot } from '@viapeople/components'
import {
	ActionBar,
	Button,
	Checkbox,
	ChevRightIcon,
	FormSignature,
	Modal,
	ModuleHeader,
	type ModuleHeaderProps,
	Text,
} from '@viapeople/components'
import { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { ModalErrorState, ModalLoadingState } from '../../../components/ModalStateHandlers'
import { useFeatureFlag } from '../../../hooks/useFeatureFlag'
import { sessionStore } from '../../../sharedStores/sessionStore'
import { useToast } from '../../../utils/ToastContext'
import { queryClient, trpc } from '../../../utils/trpc'
import { DictationReviewModal } from '../../dictation/DictationReviewModal'
import { AiSummaryList } from './AiSummaryList'
import { ComponentRenderer } from './ComponentRenderer'
import { ConfirmationDialog } from './ConfirmationDialog'
import { DeclinedReviewersNotice } from './DeclinedReviewersNotice'
import { DeclineReasonDialog } from './DeclineReasonDialog'
import { isFieldEmpty } from './isFieldEmpty'
import { PdfDownloadButton } from './PdfDownloadButton'
import { RejectionBanner } from './RejectionBanner'
import { RejectionReasonDialog } from './RejectionReasonDialog'
import type { ActionButton, EvaluateeData, JumpToItem, ProcessedFormComponent } from './types'
import { transformFormDataToResponses, useAutoSave } from './useAutoSave'
import { useConditionalVisibility } from './useConditionalVisibility'
import { useCycleExecution } from './useCycleExecution'
import { countWords, stripHtmlToText } from './wordCountUtils'

type FormValues = Record<string, string | number | boolean>

const EmployeeInfo = ({
	evaluateeData,
	evaluationHeaderConfig,
	variant = 'default',
	className,
}: {
	evaluateeData: EvaluateeData
	evaluationHeaderConfig?: Array<{
		id?: string
		name: string
		value: string
		order: number
		sources?: Array<{ type: string; key: string }>
		formId?: string | null
	}>
	variant?: 'default' | 'print'
	className?: string
}) => {
	const formatFieldValue = (field: { value: string; sources?: Array<{ type: string; key: string }> }) => {
		const rawValue = field.value
		if (!rawValue) return '-'

		const isSystemUpdatedAt = field.sources?.some((source) => source.type === 'system' && source.key === 'updatedAt')

		if (isSystemUpdatedAt) {
			const date = new Date(String(rawValue))
			if (!Number.isNaN(date.getTime())) {
				return date.toLocaleString(undefined, {
					year: 'numeric',
					month: 'short',
					day: '2-digit',
					hour: '2-digit',
					minute: '2-digit',
				})
			}
		}

		return rawValue || '-'
	}

	// Backend sends pre-resolved values, just map them (with client-side date formatting)
	const employeeInfo = evaluationHeaderConfig
		? evaluationHeaderConfig
				.sort((a, b) => a.order - b.order)
				.map((field) => ({
					label: field.name,
					value: formatFieldValue(field),
				}))
		: [
				// Fallback if no config (shouldn't happen in practice)
				{
					label: 'Employee Name',
					value: evaluateeData ? `${evaluateeData.firstName || ''} ${evaluateeData.lastName || ''}`.trim() : '-',
				},
				...(evaluateeData?.position
					? [
							{
								label: 'Position',
								value: evaluateeData?.position || '-',
							},
						]
					: []),
				...(evaluateeData?.directManager
					? [
							{
								label: 'Primary Supervisor',
								value:
									`${evaluateeData.directManager.firstName || ''} ${
										evaluateeData.directManager.lastName || ''
									}`.trim() || '-',
							},
						]
					: []),
				...(evaluateeData?.secondaryManager
					? [
							{
								label: 'Contributing Supervisor',
								value:
									`${evaluateeData.secondaryManager.firstName || ''} ${
										evaluateeData.secondaryManager.lastName || ''
									}`.trim() || '-',
							},
						]
					: []),
				...(evaluateeData?.dateOfHire
					? [
							{
								label: 'Date of Hire',
								value: evaluateeData?.dateOfHire || '-',
							},
						]
					: []),
			]

	const spacingClasses = variant === 'print' ? 'w-full p-4 mb-4' : 'm-3 p-3'

	return (
		<div
			className={`flex flex-col gap-4 rounded-md bg-blue-50 border border-blue-200 ${spacingClasses} overflow-y-visible ${
				className ?? ''
			}`.trim()}>
			{employeeInfo.map((info) => (
				<div key={info.label} className='min-w-0'>
					<Text variant='hint' className='text-neutral-400 mb-1'>
						{info.label}
					</Text>
					<Text variant='xsmall' className='break-words'>
						{info.value}
					</Text>
				</div>
			))}
		</div>
	)
}

const JumpToComponent = ({ label, icon, onClick }: { label: string; icon?: React.ReactNode; onClick: () => void }) => {
	return (
		<button
			type='button'
			className='flex items-center justify-between cursor-pointer w-full bg-transparent border-none p-0'
			onClick={onClick}>
			<div className='flex gap-1'>
				{icon}
				<Text variant='xsmall' weight='regular' className='text-blue-700 text-left'>
					{label}
				</Text>
			</div>
			<ChevRightIcon className='fill-neutral-400' />
		</button>
	)
}
const Sidebar = ({
	jumpToItems,
	evaluateeData,
	evaluationHeaderConfig,
	taskId,
	canShowPreviewToggle,
	isPreviewMode,
	onPreviewModeChange,
	coManagerResponseMode,
}: {
	jumpToItems: JumpToItem[]
	evaluateeData: EvaluateeData
	evaluationHeaderConfig?: Array<{
		id?: string
		name: string
		value: string
		order: number
		sources?: Array<{ type: string; key: string }>
		formId?: string | null
	}>
	taskId?: string
	canShowPreviewToggle?: boolean
	isPreviewMode?: boolean
	onPreviewModeChange?: (checked: boolean) => void
	coManagerResponseMode?: 'independent' | 'shared'
}) => {
	return (
		<div className='sidebar no-print flex flex-col w-70 border-r border-neutral-200 overflow-y-auto pb-44'>
			<EmployeeInfo evaluateeData={evaluateeData} evaluationHeaderConfig={evaluationHeaderConfig} />

			{coManagerResponseMode === 'shared' && (
				<div className='mx-3 mt-2 p-2 rounded-md bg-amber-50 border border-amber-200'>
					<Text variant='xsmall' weight='semibold' className='text-amber-800'>
						Shared Response Mode
					</Text>
					<Text variant='xsmall' className='text-amber-700'>
						All managers are editing the same responses. Changes are saved automatically.
					</Text>
				</div>
			)}

			{canShowPreviewToggle && (
				<div className='flex flex-col px-4 py-4 border-b border-neutral-200'>
					{/* biome-ignore lint/a11y/noLabelWithoutControl: Checkbox is a custom component that renders an input internally */}
					<label className='flex items-start gap-2 cursor-pointer'>
						<Checkbox checked={isPreviewMode} onChange={(e) => onPreviewModeChange?.(e.target.checked)} />
						<div>
							<Text variant='xsmall' weight='semibold'>
								Preview as Participant
							</Text>
							<Text variant='xsmall' className='text-neutral-500'>
								View acknowledgment step
							</Text>
						</div>
					</label>
				</div>
			)}

			{taskId && (
				<div className='flex flex-col px-4 py-4 border-b border-neutral-200'>
					<Text variant='xsmall' className='text-neutral-400 mb-3'>
						Actions
					</Text>
					<PdfDownloadButton taskId={taskId} variant='secondary' size='small' className='w-full' />
				</div>
			)}

			<div className='flex flex-col px-4 py-4 gap-2.5'>
				<Text variant='xsmall' className='text-neutral-400'>
					Jump to
				</Text>
				{jumpToItems.map((item) => (
					<JumpToComponent
						key={item.id}
						label={item.label}
						onClick={() => {
							const element = document.getElementById(item.id)
							element?.scrollIntoView({ behavior: 'smooth' })
						}}
					/>
				))}
			</div>
		</div>
	)
}

const Header = ({
	breadcrumb,
	headerActions,
	onActionClick,
}: {
	breadcrumb: ModuleHeaderProps['breadcrumb']
	headerActions: ActionButton[]
	onActionClick: (action: ActionButton) => void
}) => {
	return (
		<div className='flex justify-between items-end'>
			<div>
				<ModuleHeader breadcrumb={breadcrumb} />
			</div>
			<div className='flex gap-2'>
				{headerActions.map((action) => (
					<Button key={action.key} variant={action.variant} onClick={() => onActionClick(action)}>
						{action.label}
					</Button>
				))}
			</div>
		</div>
	)
}

export const CycleExecution = () => {
	const navigate = useNavigate()
	const { taskId } = useParams<{ taskId: string }>()

	const methods = useForm<FormValues>()
	const { handleSubmit, control, formState, watch } = methods
	const { isDirty } = formState
	const sessionState = sessionStore.getState()
	const { user } = sessionState
	const { showToast } = useToast()
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [isSigned, setIsSigned] = useState(false)
	const [signatureTimestamp, setSignatureTimestamp] = useState<Date | null>(null)
	const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false)
	const [isDeclineDialogOpen, setIsDeclineDialogOpen] = useState(false)
	const [pendingAction, setPendingAction] = useState<ActionButton | null>(null)
	const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set())
	const validatedFieldIdsRef = useRef<Set<string>>(new Set())
	const firstValidationErrorRef = useRef<string | null>(null)
	const [dictationSnapshots, setDictationSnapshots] = useState<Record<string, DictationReviewSnapshot>>({})
	const [isDictationReviewOpen, setIsDictationReviewOpen] = useState(false)
	const { isEnabled: dictationEnabled } = useFeatureFlag('voice-dictation')

	const {
		cycle,
		cycleStep,
		formComponents,
		conditionalSourceValues,
		aiSummaries,
		isCycleAdmin, // Ariel
		canEdit,
		jumpToItems,
		cycleStepActions,
		taskData,
		refetch,
		allowCAEdit,
		isLoading,
		isError,
		technicalError,
		isPreviewMode,
		setIsPreviewMode,
		coManagerResponseMode,
	} = useCycleExecution({
		taskId: taskId || '',
	})

	const evaluationId = taskData?.evaluation?.id || ''
	const currentStepId = cycleStep?.id || ''
	const userRole = taskData?.calculatedRole || ''
	const isTaskCompleted = taskData?.taskStatus === 'completed'
	const cycleStepType = taskData?.cycleStepType || ''
	const isAcknowledgeStep = cycleStepType === 'acknowledge'
	const completedAcknowledgeSignerName =
		`${taskData?.participant?.firstName || ''} ${taskData?.participant?.lastName || ''}`.trim() || 'Signature on file'
	const completedAcknowledgeSignatureTimestamp =
		taskData?.signatureTimestamp instanceof Date
			? taskData.signatureTimestamp
			: taskData?.signatureTimestamp
				? new Date(taskData.signatureTimestamp)
				: undefined

	// Show preview toggle only in Review or Release steps
	const canShowPreviewToggle = cycleStepType === 'review' || cycleStepType === 'release'

	const { hiddenComponentIds, isHidden } = useConditionalVisibility(formComponents, control, conditionalSourceValues)

	const formData = watch()
	useAutoSave({
		taskId: taskId || '',
		formData,
		isEnabled: !!taskId && canEdit && !isTaskCompleted,
		hiddenComponentIds,
	})

	useEffect(() => {
		const trackedIds = validatedFieldIdsRef.current
		if (trackedIds.size === 0) return

		setFieldErrors((prevErrors) => {
			const updatedErrors = new Set(prevErrors)
			let hasChanges = false

			const findComponent = (components: typeof formComponents, id: string): (typeof formComponents)[0] | null => {
				for (const comp of components) {
					if (comp.id === id) return comp
					if (comp.children) {
						const found = findComponent(comp.children, id)
						if (found) return found
					}
				}
				return null
			}

			// Check all fields that have ever been flagged by validation
			for (const fieldId of trackedIds) {
				const value = formData[fieldId]
				const component = findComponent(formComponents, fieldId)
				const empty = isFieldEmpty(component, value)

				if (empty && !updatedErrors.has(fieldId)) {
					updatedErrors.add(fieldId)
					hasChanges = true
				} else if (!empty && updatedErrors.has(fieldId)) {
					updatedErrors.delete(fieldId)
					hasChanges = true
				}
			}

			return hasChanges ? updatedErrors : prevErrors
		})
	}, [formData, formComponents])
	const submitFormMutation = useMutation({
		...trpc.client.tasks.submitForm.mutationOptions(),
		onSuccess: () => {
			methods.reset(methods.getValues())
			setIsSaving(false)

			const actualQueryKey = trpc.client.tasks.show.queryOptions({
				taskId: taskId ?? '',
			}).queryKey
			queryClient.invalidateQueries({
				queryKey: actualQueryKey,
			})
			queryClient.invalidateQueries({
				queryKey: ['cycleExecution', 'show'],
			})
			refetch()
			// Note: isSubmitting is not reset here because we continue to completeTaskMutation
		},
		onError: (error) => {
			console.error('Error submitting form:', error)
			setIsSaving(false)
			setIsSubmitting(false)

			const errorMessage = error.message || 'Failed to submit form'
			if (errorMessage.includes('too_big') || errorMessage.includes('1023')) {
				showToast('One or more responses exceed the 1023 character limit', 'error')
			} else {
				showToast(errorMessage, 'error')
			}
		},
	})

	const saveFormMutation = useMutation({
		...trpc.client.tasks.saveForm.mutationOptions(),
		onSuccess: () => {
			methods.reset(methods.getValues())
			setIsSaving(false)

			const actualQueryKey = trpc.client.tasks.show.queryOptions({
				taskId: taskId ?? '',
			}).queryKey
			queryClient.invalidateQueries({
				queryKey: actualQueryKey,
			})
			queryClient.invalidateQueries({
				queryKey: ['cycleExecution', 'show'],
			})
			refetch()
		},
		onError: (error) => {
			console.error('Error saving form:', error)
			setIsSaving(false)
		},
	})

	const completeTaskMutation = useMutation({
		...trpc.client.cycles.execution.completeTask.mutationOptions(),
		onSuccess: () => {
			// Reset submitting state immediately on success
			setIsSubmitting(false)

			const actualQueryKey = trpc.client.tasks.show.queryOptions({
				taskId: taskId ?? '',
			}).queryKey
			queryClient.invalidateQueries({
				queryKey: actualQueryKey,
			})
			queryClient.invalidateQueries({
				queryKey: ['cycleExecution', 'show'],
			})

			// Navigate immediately after invalidating queries
			navigate('/dashboard')
		},
		onError: (error) => {
			console.error('Error completing task:', error)
			setIsSubmitting(false)
		},
	})

	const rejectMutation = useMutation({
		...trpc.client.tasks.reject.mutationOptions(),
		onSuccess: () => {
			setIsRejectDialogOpen(false)
			navigate('/dashboard')
		},
		onError: (error) => {
			console.error('Error rejecting task:', error)
			showToast('Failed to reject evaluation', 'error')
			setIsSubmitting(false)
		},
	})

	const declineMutation = useMutation({
		...trpc.client.tasks.decline.mutationOptions(),
		onSuccess: () => {
			setIsDeclineDialogOpen(false)
			showToast('Task declined successfully')
			navigate('/dashboard')
		},
		onError: (error) => {
			console.error('Error declining task:', error)
			showToast('Failed to decline task', 'error')
			setIsSubmitting(false)
		},
	})

	const validateRequiredFields = () => {
		const formData = methods.getValues()
		const missingFields: string[] = []
		const missingFieldIds: string[] = []
		const overLimitFields: string[] = []
		const overLimitFieldIds: string[] = []

		const checkComponent = (component: ProcessedFormComponent): void => {
			if (hiddenComponentIds.has(component.id)) return

			const isValidatableField = ![
				'container',
				'heading',
				'instructions',
				'paragraph',
				'divider',
				'reviewviewer',
				'ai_summary',
				'average',
				'repeatable',
				'aggregation',
				'dataviewer',
				'external_data_view',
				'image',
			].includes(component.name)

			// Ariel: If the user is a cycle admin, bypass the validations
			if (isCycleAdmin) {
				return
			}

			// Only validate fields that the user can actually edit
			// Must have: write permission from template AND ability to edit in current step AND not be reportOnly
			if (!component.reportOnly && component.write && canEdit && isValidatableField) {
				// Check if field is required (not optional)
				if (!component.optional) {
					const value = formData[component.id]
					const isEmpty = isFieldEmpty(component, value)

					if (isEmpty) {
						const customLabel = (component.custom as { label?: string } | undefined)?.label
						missingFields.push(customLabel || component.label || component.name || 'Unknown field')
						missingFieldIds.push(component.id)
					}
				}
			}

			// Check character/word limits for textarea fields
			if (component.name === 'textarea' && component.write && canEdit && !component.reportOnly) {
				const value = formData[component.id]
				if (value && typeof value === 'string' && value.trim().length > 0) {
					const custom = component.custom as
						| { textLimit?: { isWordLimit?: boolean; words?: number; characters?: number } }
						| undefined
					const textLimit = custom?.textLimit

					if (textLimit) {
						if (textLimit.isWordLimit && textLimit.words) {
							const wordCount = countWords(value)
							if (wordCount > textLimit.words) {
								const customLabel = (component.custom as { label?: string } | undefined)?.label
								overLimitFields.push(
									`${customLabel || component.label || component.name} (${wordCount}/${textLimit.words} words)`,
								)
								overLimitFieldIds.push(component.id)
							}
						} else if (textLimit.characters) {
							const charCount = stripHtmlToText(value).length
							if (charCount > textLimit.characters) {
								const customLabel = (component.custom as { label?: string } | undefined)?.label
								overLimitFields.push(
									`${customLabel || component.label || component.name} (${charCount}/${
										textLimit.characters
									} characters)`,
								)
								overLimitFieldIds.push(component.id)
							}
						}
					}
				}
			}

			if (component.children && Array.isArray(component.children)) {
				component.children.forEach(checkComponent)
			}
		}

		formComponents.forEach(checkComponent)

		// Validate repeatable instance counts
		const repeatableErrors: string[] = []
		const repeatableErrorIds: string[] = []
		const evaluationId = taskData?.evaluation?.id

		const checkRepeatableConstraints = (component: ProcessedFormComponent): void => {
			if (component.name === 'repeatable' && component.children && component.write && canEdit) {
				const custom = component.custom as {
					label?: string
					min?: number
					max?: number
				}
				const min = custom.min ?? 1
				const max = custom.max != null && custom.max > 0 ? custom.max : undefined

				// Count instance containers (template + instances for this evaluation)
				// Template container has no evaluationSubmissionId
				// Instance containers have evaluationSubmissionId === evaluationId
				const templateContainer = component.children.find(
					(child: ProcessedFormComponent) => child.name === 'container' && !child.evaluationId,
				)
				const instanceContainers = component.children.filter(
					(child: ProcessedFormComponent) => child.name === 'container' && child.evaluationId === evaluationId,
				)

				// Total count includes template (if exists) + instance containers
				const instanceCount = (templateContainer ? 1 : 0) + instanceContainers.length

				const label = custom.label || 'Repeatable section'

				if (instanceCount < min) {
					repeatableErrors.push(`${label} requires at least ${min} instance(s), but has ${instanceCount}`)
					repeatableErrorIds.push(component.id)
				}

				if (max !== undefined && instanceCount > max) {
					repeatableErrors.push(`${label} allows at most ${max} instance(s), but has ${instanceCount}`)
					repeatableErrorIds.push(component.id)
				}
			}

			if (component.children && Array.isArray(component.children)) {
				component.children.forEach(checkRepeatableConstraints)
			}
		}

		formComponents.forEach(checkRepeatableConstraints)

		if (missingFields.length > 0 || overLimitFields.length > 0 || repeatableErrors.length > 0) {
			const errorIds = new Set([...missingFieldIds, ...overLimitFieldIds, ...repeatableErrorIds])
			for (const id of errorIds) {
				validatedFieldIdsRef.current.add(id)
			}
			firstValidationErrorRef.current = missingFieldIds[0] ?? overLimitFieldIds[0] ?? repeatableErrorIds[0] ?? null
			setFieldErrors(errorIds)

			if (missingFields.length > 0) {
				const MAX_FIELDS_TO_SHOW = 1
				const displayedFields = missingFields.slice(0, MAX_FIELDS_TO_SHOW)
				const remainingCount = missingFields.length - MAX_FIELDS_TO_SHOW
				const fieldsList =
					remainingCount > 0 ? `${displayedFields.join(', ')} and ${remainingCount} more` : displayedFields.join(', ')
				showToast(`Please fill in the following required fields: ${fieldsList}`, 'error')
			} else if (overLimitFields.length > 0) {
				const MAX_FIELDS_TO_SHOW = 1
				const displayedFields = overLimitFields.slice(0, MAX_FIELDS_TO_SHOW)
				const remainingCount = overLimitFields.length - MAX_FIELDS_TO_SHOW
				const fieldsList =
					remainingCount > 0 ? `${displayedFields.join(', ')} and ${remainingCount} more` : displayedFields.join(', ')
				showToast(
					`The following fields exceed their limits: ${fieldsList}. Please reduce the text before submitting.`,
					'error',
				)
			} else if (repeatableErrors.length > 0) {
				showToast(`Repeatable section validation failed: ${repeatableErrors.join('; ')}`, 'error')
			}
			return false
		}

		setFieldErrors(new Set())
		return true
	}

	const handleDictationSnapshot = (snapshot: DictationReviewSnapshot) => {
		setDictationSnapshots((prev) => ({ ...prev, [snapshot.fieldId]: snapshot }))
	}

	const handleSubmitDraftCommand = () => {
		if (!isSaving && !submitFormMutation.isPending) {
			handleSave()
		}
	}

	const performSubmit = () => {
		if (!taskId) return

		setIsSubmitting(true)

		const formData = methods.getValues()

		// Only submit values for components where the user has write permission
		const getAllWritableComponents = (components: typeof formComponents): Set<string> => {
			const writableIds = new Set<string>()
			const checkComponent = (comp: (typeof formComponents)[0]) => {
				if (comp.write && !comp.reportOnly) {
					writableIds.add(comp.id)
				}
				if (comp.children && Array.isArray(comp.children)) {
					comp.children.forEach(checkComponent)
				}
			}
			components.forEach(checkComponent)
			return writableIds
		}
		const writableComponentIds = getAllWritableComponents(formComponents)

		const responses = transformFormDataToResponses(formData, hiddenComponentIds).filter(({ componentId }) =>
			writableComponentIds.has(componentId),
		)

		// Collect repeatable instance counts
		const collectRepeatableInstances = (components: typeof formComponents) => {
			const instances: Array<{ repeatableComponentId: string; instanceCount: number }> = []
			const evaluationId = taskData?.evaluation?.id

			const checkComponent = (comp: (typeof formComponents)[0]) => {
				if (comp.name === 'repeatable' && comp.children) {
					// Count instance containers (template + instances for this evaluation)
					const templateContainer = comp.children.find(
						(child: (typeof formComponents)[0]) => child.name === 'container' && !child.evaluationSubmissionId,
					)
					const instanceContainers = comp.children.filter(
						(child: (typeof formComponents)[0]) =>
							child.name === 'container' && child.evaluationSubmissionId === evaluationId,
					)

					// Total count includes template (if exists) + instance containers
					const instanceCount = (templateContainer ? 1 : 0) + instanceContainers.length

					instances.push({
						repeatableComponentId: comp.id,
						instanceCount,
					})
				}
				if (comp.children && Array.isArray(comp.children)) {
					comp.children.forEach(checkComponent)
				}
			}

			components.forEach(checkComponent)
			return instances
		}
		const repeatableInstances = collectRepeatableInstances(formComponents)

		submitFormMutation.mutate(
			{ taskId, responses, repeatableInstances },
			{
				onSuccess: () => {
					completeTaskMutation.mutate({
						taskId,
						signatureTimestamp: isSigned ? signatureTimestamp || new Date() : undefined,
					})
				},
			},
		)
	}

	const handleActionClick = (action: ActionButton) => {
		// Prevent double-clicks by checking if already submitting or saving
		// Also check mutation pending state to prevent race conditions
		if (isSubmitting || isSaving || submitFormMutation.isPending || completeTaskMutation.isPending) {
			return
		}

		const submitKeys = ['submit', 'approve', 'release', 'acknowledge']
		if (submitKeys.includes(action.key) && taskId) {
			if (!validateRequiredFields()) {
				return
			}
			if (dictationEnabled && Object.keys(dictationSnapshots).length > 0) {
				setPendingAction(action)
				setIsDictationReviewOpen(true)
				return
			}
			// Show confirmation dialog before submitting
			setPendingAction(action)
		} else {
			if (action.key === 'reject') {
				setIsRejectDialogOpen(true)
			} else if (action.key === 'decline') {
				setIsDeclineDialogOpen(true)
			}
			setTimeout(() => {
				if (action.movesForward) {
					setTimeout(() => {
						navigate('/dashboard')
					}, 1500)
				}
			}, 1000)
		}
	}
	const handleReject = (rejectionReason?: string) => {
		setIsSubmitting(true)
		rejectMutation.mutate({
			taskId: taskId || '',
			rejectionReason,
			signatureTimestamp: isSigned ? signatureTimestamp || new Date() : undefined,
		})
	}

	const handleDecline = (declineReason?: string) => {
		setIsSubmitting(true)
		declineMutation.mutate({
			taskId: taskId || '',
			declineReason,
		})
	}

	const handleConfirmAction = () => {
		if (!validateRequiredFields()) {
			setPendingAction(null)
			const errorId = firstValidationErrorRef.current
			if (errorId) {
				setTimeout(() => {
					document.getElementById(errorId)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
				}, 0)
			}
			return
		}
		performSubmit()
		setPendingAction(null)
		setDictationSnapshots({})
	}

	const handleCancelAction = () => {
		setPendingAction(null)
	}

	const getActionLabel = (action: ActionButton): string => {
		return action.label.toLowerCase()
	}

	const handleSave = () => {
		if (!taskId) return

		setIsSaving(true)

		// Get form data and convert to our API format
		const formData = methods.getValues()

		// Only save values for components where the user has write permission
		const getAllWritableComponents = (components: typeof formComponents): Set<string> => {
			const writableIds = new Set<string>()
			const checkComponent = (comp: (typeof formComponents)[0]) => {
				if (comp.write && !comp.reportOnly) {
					writableIds.add(comp.id)
				}
				if (comp.children && Array.isArray(comp.children)) {
					comp.children.forEach(checkComponent)
				}
			}
			components.forEach(checkComponent)
			return writableIds
		}
		const writableComponentIds = getAllWritableComponents(formComponents)

		const responses = transformFormDataToResponses(formData, hiddenComponentIds).filter(({ componentId }) =>
			writableComponentIds.has(componentId),
		)

		// Save form (no validation, allows long text)
		saveFormMutation.mutate({ taskId, responses })
	}

	const onSubmit = (_data: FormValues) => {
		const submitAction = cycleStepActions?.bottomActions.find((action) => action.key === 'submit')
		if (submitAction) {
			handleActionClick(submitAction)
		}
	}

	const handleCancel = () => {
		navigate('/dashboard')
	}

	const onSign = () => {
		if (!isDirty && !isSigned) {
			setIsSigned(true)
			setSignatureTimestamp(new Date())
		}
	}

	const handleSignatureFieldAction = (action: ActionButton) => {
		if (!isDirty && isSigned) {
			handleActionClick(action)
		}
	}

	useEffect(() => {
		if (isDirty && isSigned) {
			setIsSigned(false)
			setSignatureTimestamp(null)
		}
	}, [isDirty, isSigned])

	const createBottomActions = () => {
		const actions = [...(cycleStepActions ? cycleStepActions.bottomActions : [])]
		// Show "Close" for completed tasks viewed by non-CAs, "Cancel" for active tasks
		const cancelAction = {
			label: isTaskCompleted && !canEdit ? 'Close' : 'Cancel',
			onClick: handleCancel,
			variant: 'secondary' as const,
			disabled: isSubmitting || isSaving || submitFormMutation.isPending || completeTaskMutation.isPending,
		}

		// Only block completed tasks for non-CAs (who have read access)
		// CAs (who have write access) can still save on completed tasks
		// Backend also allows this via permissions.admin check in saveForm
		if (isTaskCompleted && !canEdit) {
			return [cancelAction]
		}

		if (isDirty && canEdit) {
			const saveAction = {
				label: 'Save',
				onClick: handleSave,
				variant: 'primary' as const,
				disabled: isSaving,
			}

			const disabledCycleActions = actions.map((action) => ({
				label: action.label,
				onClick: () => handleActionClick(action),
				variant: action.variant || 'secondary',
				disabled: true,
			}))
			return [cancelAction, ...disabledCycleActions, saveAction]
		}

		// Normal behavior when form is not dirty
		const cycleActions = actions.map((action) => {
			// For review step without signature requirement, enable approve/reject actions
			const isReviewAction = cycleStep?.name === 'Review' && (action.key === 'approve' || action.key === 'reject')
			const shouldEnableReviewAction =
				isReviewAction && !taskData?.requireSignature && (taskData?.cycleActions?.length ?? 0) > 0

			return {
				label: action.label,
				onClick: () => handleActionClick(action),
				variant: action.variant || 'secondary',
				disabled:
					isSubmitting ||
					isSaving ||
					submitFormMutation.isPending ||
					completeTaskMutation.isPending ||
					(!canEdit && !shouldEnableReviewAction),
			}
		})

		return [cancelAction, ...cycleActions]
	}

	if (isLoading) {
		return <ModalLoadingState title={cycleStep?.name || 'Loading...'} onClose={() => navigate(-1)} />
	}

	if (isError) {
		return (
			<ModalErrorState
				errorMessage='Unable to load form'
				errorDescription='Something went wrong loading this form. Please try again or contact support if the issue persists.'
				technicalDetails={technicalError}
				logoUrl={sessionState.organization?.logo ?? undefined}
				onClose={() => navigate(-1)}
				onRetry={() => refetch()}
			/>
		)
	}

	return (
		<Modal
			isOpen={true}
			onClose={() => navigate(-1)}
			fullContent={true}
			fullScreen={true}
			title={cycleStep?.name || ''}
			bodyClassName='h-full'>
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)} className='h-full'>
					<div className='flex flex-col h-full'>
						<div className='flex flex-1 overflow-hidden'>
							<Sidebar
								jumpToItems={jumpToItems.filter((item) => !hiddenComponentIds.has(item.id))}
								evaluateeData={taskData?.participant ?? {}}
								evaluationHeaderConfig={taskData?.evaluationHeaderConfig}
								taskId={taskId}
								canShowPreviewToggle={canShowPreviewToggle}
								isPreviewMode={isPreviewMode}
								onPreviewModeChange={setIsPreviewMode}
								coManagerResponseMode={coManagerResponseMode}
							/>
							<div className='cycle-execution-content flex flex-col flex-1 gap-4 p-6 overflow-y-auto pb-44'>
								<Header
									breadcrumb={[
										{ label: 'Dashboard', to: '/dashboard' },
										{
											label: cycle?.name || '',
										},
										{
											label: cycleStep?.name || '',
										},
									]}
									headerActions={cycleStepActions?.headerActions ?? []}
									onActionClick={handleActionClick}
								/>

								<EmployeeInfo
									evaluateeData={taskData?.participant ?? {}}
									evaluationHeaderConfig={taskData?.evaluationHeaderConfig}
									variant='print'
									className='print-only'
								/>

								{taskData.rejection && (
									<RejectionBanner
										rejectionReason={taskData.rejection?.rejectionReason}
										rejectedBy={`${taskData.rejection?.rejectedBy?.firstName} ${taskData.rejection?.rejectedBy?.lastName}`}
									/>
								)}

								{taskData.declinedReviewers && taskData.declinedReviewers.length > 0 && (
									<DeclinedReviewersNotice declinedReviewers={taskData.declinedReviewers} />
								)}

								{formComponents.map((component: (typeof formComponents)[0]) => {
									// Check if any summaries should appear after this specific component (exact ID match)
									const summariesAfterThis =
										aiSummaries?.filter((summary) => {
											const displayAfter = summary.config?.displayAfterComponentId
											return displayAfter && displayAfter === component.id
										}) || []

									return (
										<div key={component.id}>
											<ComponentRenderer
												component={component}
												control={control}
												evaluationId={evaluationId}
												currentStepId={currentStepId}
												userRole={userRole}
												canEdit={canEdit}
												allowCAEdit={allowCAEdit}
												taskId={taskId}
												refetch={refetch}
												fieldErrors={fieldErrors}
												allFormComponents={formComponents}
												aiSummaries={aiSummaries}
												onDictationSnapshot={handleDictationSnapshot}
												onSubmitDraft={handleSubmitDraftCommand}
												isHidden={isHidden}
												evaluateeExternalId={
													typeof (taskData?.participant as { externalId?: unknown } | undefined)?.externalId ===
													'string'
														? ((taskData?.participant as { externalId?: unknown } | undefined)?.externalId as string)
														: undefined
												}
											/>

											{summariesAfterThis.length > 0 && (
												<div className='my-6'>
													<AiSummaryList aiSummaries={summariesAfterThis} evaluationId={evaluationId} />
												</div>
											)}
										</div>
									)
								})}

								{aiSummaries &&
									aiSummaries.length > 0 &&
									(() => {
										const bottomSummaries = aiSummaries.filter(
											(summary) =>
												!summary.config?.displayAfterComponentId || summary.config?.displayAfterComponentId === null,
										)

										return bottomSummaries.length > 0 ? (
											<div className='mt-6 mb-6'>
												<AiSummaryList aiSummaries={bottomSummaries} evaluationId={evaluationId} />
											</div>
										) : null
									})()}

								{!isTaskCompleted && (cycleStepActions?.signatureActions ?? []).length > 0 && (
									<FormSignature
										signature={`${user?.firstName || ''} ${user?.lastName || ''}`}
										actionTitle={(cycleStepActions?.signatureActions ?? [])[0]?.label || 'Sign and Submit'}
										onAction={() => handleSignatureFieldAction((cycleStepActions?.signatureActions ?? [])[0])}
										disabled={isDirty || isSubmitting || isSaving}
										isSigned={isSigned}
										onSign={onSign}
										signatureTimestamp={signatureTimestamp || taskData?.signatureTimestamp || undefined}
									/>
								)}

								{isTaskCompleted && taskData?.requireSignature && isAcknowledgeStep && (
									<FormSignature
										signature={completedAcknowledgeSignerName}
										actionTitle='Acknowledged'
										readOnly
										isSigned={true}
										onSign={() => {}}
										onAction={() => {}}
										signatureTimestamp={completedAcknowledgeSignatureTimestamp}
										timestampUnavailableText={
											completedAcknowledgeSignatureTimestamp ? undefined : 'Timestamp unavailable'
										}
									/>
								)}
							</div>
						</div>

						{((cycleStepActions?.bottomActions ?? []).length > 0 || true) && (
							<ActionBar variant='modal' className='left-0' rightActions={createBottomActions()} />
						)}
					</div>
				</form>
			</FormProvider>
			<RejectionReasonDialog
				isOpen={isRejectDialogOpen}
				onConfirm={handleReject}
				onClose={() => setIsRejectDialogOpen(false)}
				isSubmitting={isSubmitting}
			/>
			<DeclineReasonDialog
				isOpen={isDeclineDialogOpen}
				onConfirm={handleDecline}
				onClose={() => setIsDeclineDialogOpen(false)}
				isSubmitting={isSubmitting}
				requireReason={taskData?.requireDeclineReason ?? false}
			/>
			{pendingAction && !isDictationReviewOpen && (
				<ConfirmationDialog
					isOpen={!!pendingAction}
					onConfirm={handleConfirmAction}
					onClose={handleCancelAction}
					isSubmitting={isSubmitting}
					title={`Confirm ${pendingAction.label}`}
					message={`Are you sure you want to ${getActionLabel(pendingAction)}?`}
					confirmLabel={pendingAction.label}
					confirmVariant='primary'
				/>
			)}
			<DictationReviewModal
				isOpen={isDictationReviewOpen}
				snapshots={Object.values(dictationSnapshots)}
				onClose={() => {
					setIsDictationReviewOpen(false)
					setPendingAction(null)
				}}
				onConfirm={() => {
					setIsDictationReviewOpen(false)
					if (pendingAction) {
						handleConfirmAction()
					}
				}}
			/>
		</Modal>
	)
}
